import { defineSemanticTokens } from '@chakra-ui/react';

export const colors = defineSemanticTokens.colors({
  bg: {
    // ── Solid surfaces (brand HSL scale) ──
    DEFAULT: {
      value: '{colors.brand.900}',
    },
    surface: {
      value: '{colors.brand.800}',
    },
    card: {
      value: '{colors.brand.700}',
    },
    emphasized: {
      value: '{colors.brand.600}',
    },
    inverted: {
      value: '{colors.white}',
    },
    // ── Transparent overlays (whiteAlpha) ──
    muted: {
      value: '{colors.whiteAlpha.100}',
    },
    // Заливка пилюли. Своё значение, а не ступень whiteAlpha: на тёмном шаги шкалы прыгают мимо
    // нужного места. Подбиралось сверху вниз, с замером на каждом шаге: 0.16 — 1.60:1 к странице,
    // кричит громче собственного текста; 0.12 — 1.39:1, всё ещё ярче соседних фактов.
    //
    // 0.09 держалось долго, но у него был не замеченный замером сосед: заливка игрового тега
    // (`bg.emphasized`). На карточке ленты они давали #313237 против #2d2f39 — 0.27 пункта
    // яркости, то есть один и тот же серый. Тег и чип отвечают на разные вопросы («по этому
    // ищут» против «вот факт о человеке») и обязаны различаться материалом, а не только тем,
    // что написано внутри.
    //
    // 0.05 — это 1.15:1 к карточке и 1.12:1 к странице: форма пилюли читается, но она заметно
    // темнее тега (1.10:1 между их заливками). Ниже не опускать: на 0.04 пилюля пропадает и
    // остаются висеть значок с подписью. Темнее фона тоже нельзя — поверхности здесь почти
    // чёрные, и затемнённая пилюля на странице даёт 1.03:1, то есть исчезает вовсе.
    //
    // Полупрозрачность обязательна: сплошной токен рано или поздно совпадёт с поверхностью,
    // на которую пилюля легла.
    chip: {
      value: 'rgba(255, 255, 255, 0.05)',
    },
    // Заливка скелетона. Не bg.emphasized (brand.600): заглушка на нём получается СВЕТЛЕЕ
    // карточки, которую заменяет (bg.card = brand.700), и ярче страницы (brand.900) — пустая
    // лента выглядела подсвеченной, хотя ждать нечего. Полупрозрачный белый на полтона выше
    // страницы: форма карточки читается, но в глаза не бьёт и работает на любой поверхности.
    skeleton: {
      value: 'rgba(255, 255, 255, 0.05)',
    },
    overlay: {
      value: 'hsla(228, 11.6%, 20%, 0.5)',
    },
    error: {
      value: {
        _light: '{colors.red.50}',
        _dark: '{colors.red.950}',
      },
    },
    warning: {
      value: {
        _light: '{colors.orange.50}',
        _dark: '{colors.orange.950}',
      },
    },
    success: {
      value: {
        _light: '{colors.green.50}',
        _dark: '{colors.green.950}',
      },
    },
    info: {
      value: {
        _light: '{colors.blue.50}',
        _dark: '{colors.blue.950}',
      },
    },
  },
  fg: {
    DEFAULT: {
      value: '{colors.whiteAlpha.900}',
    },
    emphasized: {
      value: '{colors.whiteAlpha.800}',
    },
    secondary: {
      value: '{colors.whiteAlpha.700}',
    },
    // 0.36 давало 3.33:1 на основном фоне: подписи, счётчики и таймеры не проходили
    // AA (4.5:1 для текста мельче 18px). 0.48 это ~5:1 там же, и остаётся заметно
    // тише fg.secondary (0.64), так что иерархия не схлопывается.
    muted: {
      value: '{colors.whiteAlpha.600}',
    },
    subtle: {
      value: '{colors.whiteAlpha.300}',
    },
    // Цвет линейных иконок — и единственный в шкале fg, который НЕПРОЗРАЧЕН.
    //
    // Остальные ступени это whiteAlpha, то есть rgba с альфой. Тексту это безразлично: глифы не
    // накладываются друг на друга. А иконка lucide — три-четыре отдельных штриха в одной группе,
    // и там, где они пересекаются (голова и плечи, лупа и её ручка), две полупрозрачные заливки
    // складываются: 0.48 поверх 0.48 даёт ~0.73, и пересечения выходят в полтора раза плотнее
    // остального контура. Иконка читается пятнистой, будто недорисованной.
    //
    // Значение подобрано так, чтобы совпасть с тем, как fg.muted ВЫГЛЯДИТ на фоне страницы
    // (48% белого поверх brand.900), но без альфы — тогда наложение штрихов ничего не меняет.
    //
    // 60%, а не 52%: калибровка по СТРАНИЦЕ не учла, что чаще всего иконка стоит не на ней.
    // В чипе под иконкой ещё и заливка `bg.chip` (9% белого), в пункте меню — `bg.card`; фон
    // под значком светлее фона, по которому подбирали цвет, и те же 52% давали 3.66:1 в чипе,
    // то есть значок почти вдвое тише собственной подписи — ряд контактов читался выключенным.
    //
    // Значение привязано к подписи чипа и держится ступенью ниже неё: 68% её яркости. Это
    // 4.5:1 в чипе, 5.8:1 на карточке и 6.5:1 на странице — везде выше порога AA.
    //
    // Насыщенность 1.2%, а не 11.6% как у фонов brand: та же насыщенность работает на светлоте
    // 8–12%, где подмес не виден, но на 52% даёт #767c93 — синий канал на 29 пунктов выше
    // красного, то есть отчётливый сине-фиолетовый отлив. Здесь разбег каналов 3 пункта.
    icon: {
      value: 'hsl(220, 1.2%, 60%)',
    },
    inverted: {
      value: '{colors.black}',
    },
    error: {
      value: '{colors.red.400}',
    },
    warning: {
      value: '{colors.orange.200}',
    },
    success: {
      value: '{colors.green.400}',
    },
    info: {
      value: '{colors.blue.300}',
    },
    link: {
      value: '{colors.teal.300}',
    },
    'link.hover': {
      value: '{colors.teal.200}',
    },
  },
  border: {
    DEFAULT: {
      value: '{colors.whiteAlpha.200}',
    },
    subtle: {
      value: '{colors.whiteAlpha.100}',
    },
    muted: {
      value: '{colors.whiteAlpha.100}',
    },
    inverted: {
      value: '{colors.gray.200}',
    },
    error: {
      value: {
        _light: '{colors.red.500}',
        _dark: '{colors.red.400}',
      },
    },
    warning: {
      value: {
        _light: '{colors.orange.500}',
        _dark: '{colors.orange.400}',
      },
    },
    success: {
      value: {
        _light: '{colors.green.500}',
        _dark: '{colors.green.400}',
      },
    },
    info: {
      value: {
        _light: '{colors.blue.500}',
        _dark: '{colors.blue.400}',
      },
    },
  },
  gray: {
    contrast: {
      value: '{colors.black}',
    },
    fg: {
      value: '{colors.gray.200}',
    },
    subtle: {
      value: '{colors.gray.800}',
    },
    muted: {
      value: {
        _light: '{colors.gray.200}',
        _dark: '{colors.gray.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.gray.300}',
        _dark: '{colors.gray.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.gray.900}',
        _dark: '{colors.white}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.gray.400}',
        _dark: '{colors.gray.400}',
      },
    },
  },
  red: {
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.red.700}',
        _dark: '{colors.red.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.red.100}',
        _dark: '{colors.red.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.red.200}',
        _dark: '{colors.red.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.red.300}',
        _dark: '{colors.red.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.red.500}',
        _dark: '{colors.red.500}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.red.500}',
        _dark: '{colors.red.500}',
      },
    },
  },
  orange: {
    contrast: {
      value: {
        _light: 'white',
        _dark: 'black',
      },
    },
    fg: {
      value: {
        _light: '{colors.orange.700}',
        _dark: '{colors.orange.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.orange.100}',
        _dark: '{colors.orange.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.orange.200}',
        _dark: '{colors.orange.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.orange.300}',
        _dark: '{colors.orange.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.orange.400}',
        _dark: '{colors.orange.400}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.orange.500}',
        _dark: '{colors.orange.500}',
      },
    },
  },
  green: {
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.green.700}',
        _dark: '{colors.green.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.green.100}',
        _dark: '{colors.green.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.green.200}',
        _dark: '{colors.green.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.green.300}',
        _dark: '{colors.green.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.green.600}',
        _dark: '{colors.green.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.green.500}',
        _dark: '{colors.green.500}',
      },
    },
  },
  blue: {
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.blue.700}',
        _dark: '{colors.blue.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.blue.100}',
        _dark: '{colors.blue.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.blue.200}',
        _dark: '{colors.blue.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.blue.300}',
        _dark: '{colors.blue.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.blue.600}',
        _dark: '{colors.blue.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.blue.500}',
        _dark: '{colors.blue.500}',
      },
    },
  },
  yellow: {
    contrast: {
      value: {
        _light: 'black',
        _dark: 'black',
      },
    },
    fg: {
      value: {
        _light: '{colors.yellow.800}',
        _dark: '{colors.yellow.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.yellow.100}',
        _dark: '{colors.yellow.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.yellow.200}',
        _dark: '{colors.yellow.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.yellow.300}',
        _dark: '{colors.yellow.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.yellow.300}',
        _dark: '{colors.yellow.300}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.yellow.500}',
        _dark: '{colors.yellow.500}',
      },
    },
  },
  teal: {
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.teal.700}',
        _dark: '{colors.teal.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.teal.100}',
        _dark: '{colors.teal.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.teal.200}',
        _dark: '{colors.teal.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.teal.300}',
        _dark: '{colors.teal.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.teal.600}',
        _dark: '{colors.teal.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.teal.500}',
        _dark: '{colors.teal.500}',
      },
    },
  },
  purple: {
    contrast: {
      value: 'white',
    },
    fg: {
      value: '{colors.purple.200}',
    },
    subtle: {
      value: '{colors.brand.700}',
    },
    muted: {
      value: '{colors.purple.200}',
    },
    emphasized: {
      value: '{colors.purple.700}',
    },
    // purple.200 against `contrast: white` is 4.23:1 — under AA, which is why solid
    // brand buttons were avoided and Bump hand-rolls bgColor="purple.500" instead.
    // purple.500 is the shade that workaround already proved: 7.68:1.
    solid: {
      value: '{colors.purple.500}',
    },
    focusRing: {
      value: '{colors.purple.500}',
    },
  },
  pink: {
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.pink.700}',
        _dark: '{colors.pink.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.pink.100}',
        _dark: '{colors.pink.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.pink.200}',
        _dark: '{colors.pink.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.pink.300}',
        _dark: '{colors.pink.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.pink.600}',
        _dark: '{colors.pink.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.pink.500}',
        _dark: '{colors.pink.500}',
      },
    },
  },
  cyan: {
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.cyan.700}',
        _dark: '{colors.cyan.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.cyan.100}',
        _dark: '{colors.cyan.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.cyan.200}',
        _dark: '{colors.cyan.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.cyan.300}',
        _dark: '{colors.cyan.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.cyan.600}',
        _dark: '{colors.cyan.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.cyan.500}',
        _dark: '{colors.cyan.500}',
      },
    },
  },

  // ── Presence / liveness family ──
  // Один смысл «сейчас/в сети/играет» → один набор токенов. Используют: онлайн-точка
  // аватара, presence-пилюль «играет сейчас», будущие «в сессии»-чипы.
  // ── Цвета площадок ──
  // Фирменный цвет внешней платформы — единственный случай, когда цвет приходит не от нас.
  // Пока это Discord: он значит «разговор идёт там», и человек узнаёт его раньше, чем читает
  // подпись. Синий был вбит числом в пяти местах, и когда понадобилось поправить оттенок,
  // правок оказалось пять вместо одной; шестнадцатеричное число вдобавок не переживает смену
  // темы и не находится поиском по токену.
  //
  // Набор тот же, что у присутствия: заливка, текст на подложке, сама подложка. Steam и Riot
  // лягут сюда же, когда понадобятся.
  platform: {
    discord: {
      // Знак и акцент: точка, иконка, залитая кнопка «зайти в комнату».
      solid: {
        value: '#5865F2',
      },
      // Текст и иконка на приглушённой подложке.
      fg: {
        value: '#5865F2',
      },
      // Подложка чипа «Discord». 15% — та же плотность, что у статуса «идёт сейчас»:
      // площадка называет себя, но не спорит с содержимым строки.
      bg: {
        value: 'color-mix(in srgb, #5865F2 15%, transparent)',
      },
      // Тише подложки: панель голосовой комнаты занимает целый блок, и на 15% она читалась
      // синим полем, а не фоном под текстом.
      subtle: {
        value: 'color-mix(in srgb, #5865F2 8%, transparent)',
      },
      // Край панели: тот же цвет, но виден на любой поверхности.
      border: {
        value: 'color-mix(in srgb, #5865F2 20%, transparent)',
      },
    },
  },

  status: {
    live: {
      // Заливка (онлайн-точка, live-индикаторы)
      solid: {
        value: '{colors.green.400}',
      },
      // Текст/иконка на подложке (контрастнее)
      fg: {
        value: '{colors.green.300}',
      },
      // Приглушённая подложка пилюля
      bg: {
        value: 'color-mix(in srgb, var(--chakra-colors-green-400) 15%, transparent)',
      },
    },
  },
});
